import {Chip, Stack} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import * as analytics from 'analytics/Analytics';
import {ANALYTICS_MODULE} from 'analytics/AnalyticsTypes';
import {ACTION_CATEGORY} from 'constants/AnalyticsConstants';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import SelectableCard from 'ui-library/inputs/SelectableCard';

import {AppOrVersion, ButtonType} from './CreateAppTypes';

export enum LegacyCreateAppChoiceButtonType {
  FROM_SCRATCH,
  FROM_DRAFT,
  FROM_MANIFEST,
}

const LEGACY_BUTTON_TYPE_FOR_ANALYTICS: Record<ButtonType, LegacyCreateAppChoiceButtonType> = {
  [ButtonType.FROM_SCRATCH]: LegacyCreateAppChoiceButtonType.FROM_SCRATCH,
  [ButtonType.FROM_DRAFT]: LegacyCreateAppChoiceButtonType.FROM_DRAFT,
  [ButtonType.FROM_MANIFEST]: LegacyCreateAppChoiceButtonType.FROM_MANIFEST,
};

function trackCreateAppChoice(type: ButtonType): void {
  analytics.trackEventWithModule(
    {
      module: ANALYTICS_MODULE.BENCHLING_APPS,
      action: 'clicked_create_app_choice',
      actionCategory: ACTION_CATEGORY.READ,
    },
    ANALYTICS_MODULE.BENCHLING_APPS,
    {buttonType: LEGACY_BUTTON_TYPE_FOR_ANALYTICS[type]}
  );
}

interface CreateAppChoiceButtonsProps {
  description?: string;
  appOrVersion: AppOrVersion;
  onChangeType: (button: ButtonType) => void;
}

export default function CreateAppChoiceButtons(props: CreateAppChoiceButtonsProps): JSX.Element {
  const av = props.appOrVersion;
  const scratchOrDraft = av === AppOrVersion.APP ? ButtonType.FROM_SCRATCH : ButtonType.FROM_DRAFT;
  const [createType, setCreateType] = useState(scratchOrDraft);

  const isApp = av === AppOrVersion.APP;

  return (
    <Col lg={4} className={css(styles.leftContainer)}>
      {props.description && <Row className={css(styles.leftCol)}>{props.description}</Row>}
      <Row className={css(styles.leftCol)}>
        <Chip.Group
          multiple={false}
          value={createType}
          onChange={(v) => {
            if (v == null || v === '') {
              return;
            }
            const type = v as ButtonType;
            setCreateType(type);
            props.onChangeType(type);
            trackCreateAppChoice(type);
          }}
        >
          <Stack spacing="md" aria-label="Creation source">
            <SelectableCard
              value={scratchOrDraft}
              title={isApp ? 'From scratch' : 'From existing'}
              description={
                isApp
                  ? 'Define your app using the form—name, description, and audience.'
                  : 'Copy details from your latest draft into a new version.'
              }
              dataTestAttrs={{
                component: 'CreateAppChoiceButtons',
                element: scratchOrDraft,
              }}
            />
            <SelectableCard
              value={ButtonType.FROM_MANIFEST}
              title="From manifest"
              description={
                isApp
                  ? 'Upload a YAML manifest to create the app definition and configuration.'
                  : 'Upload a YAML manifest to create this draft version.'
              }
              dataTestAttrs={{
                component: 'CreateAppChoiceButtons',
                element: ButtonType.FROM_MANIFEST,
              }}
            />
          </Stack>
        </Chip.Group>
      </Row>
    </Col>
  );
}

const styles = StyleSheet.create({
  leftCol: {
    padding: '1em',
  },
  leftContainer: {
    paddingTop: 16,
    paddingBottom: 16,
  },
});
